import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import {
  APPLICATION_DEFAULT_DATA,
  APPLICATION_SET_DEFAULT_DATA,
  ClusterConfiguration,
  SourceConfiguration,
  DaasApplicationInputData,
  DaasApplicationSetInputData,
  ImageUpdaterConfig,
  Project,
  Cluster,
  RepoType,
  SyncPolicy,
  KubernetesMetadata,
} from './types';

export function inferRepoType(url: string): RepoType | null {
  if (!url) return null;
  const literalPrefix = url.includes('{{') ? url.split('{{')[0] : url;
  try {
    const u = new URL(literalPrefix);
    if (u.protocol === 'oci:') return 'oci';
    if (u.host === 'github.com') return 'git';
    return 'helm';
  } catch {
    return url.includes('{{') ? null : 'helm';
  }
}

export function resolveRepoType(source: SourceConfiguration): RepoType {
  if (
    source.deploymentType === 'manifests' ||
    source.deploymentType === 'kustomize'
  ) {
    return 'git';
  }
  return inferRepoType(source.repoURL) ?? source.repoType ?? 'git';
}

const decodeBase64 = (encodedStr: string): string => {
  let value: string = '';
  try {
    if (typeof encodedStr !== 'string') {
      return '';
    }
    if (encodedStr) {
      value = Buffer.from(encodedStr, 'base64').toString('utf-8');
    }
    return value;
  } catch (e) {
    return '';
  }
};

export const serializeProjectEntity = (entity: Entity): Project => {
  return {
    id: `${entity.kind}:${entity.metadata.namespace || 'default'}/${
      entity.metadata.name
    }`,
    name: entity.metadata.name,
    title: entity.metadata.title || entity.metadata.name,
    clusters: Array.isArray(entity.spec?.clusters)
      ? (entity.spec.clusters as Cluster[])
      : [],
  };
};

export const convertEntityToApplicationSetInput = (
  entity: Entity,
): Partial<DaasApplicationSetInputData> => ({
  applicationSetName: (entity?.spec?.applicationSetName as string) || '',
  description: (entity.metadata.description as string) || '',
  owner: `group:default/${entity?.spec?.owner}`,
  project: (entity?.spec?.projectId as string) || '',
  appNameTemplate: (entity?.spec?.appNameTemplate as string) || '',
  generators: decodeBase64(String(entity?.spec?.generators || '')),
  cluster: (entity?.spec?.clusterConfig as ClusterConfiguration) || null,
  source: (entity?.spec?.source as SourceConfiguration) || null,
  syncPolicy:
    (entity?.spec?.syncPolicy as SyncPolicy) ||
    APPLICATION_SET_DEFAULT_DATA.syncPolicy,
  applicationMetadata: (entity?.spec
    ?.applicationMetadata as KubernetesMetadata) || {
    annotations: [],
    labels: [],
  },
  templatePatch: entity?.spec?.templatePatch
    ? decodeBase64(String(entity?.spec?.templatePatch))
    : null,
  imageUpdater: entity?.spec?.imageUpdaterConfig as
    | ImageUpdaterConfig
    | undefined,
  applicationSetRef: stringifyEntityRef(entity),
});

export const convertEntityToApplicationInput = (
  entity: Entity,
): Partial<DaasApplicationInputData> => {
  return {
    applicationName: (entity?.spec?.applicationName as string) || '',
    description: (entity.metadata.description as string) || '',
    owner: `group:default/${entity?.spec?.owner}`,
    project: (entity?.spec?.projectId as string) || '',
    cluster: (entity?.spec?.clusterConfig as ClusterConfiguration) || null,
    source: (entity?.spec?.source as SourceConfiguration) || null,
    syncPolicy:
      (entity?.spec?.syncPolicy as SyncPolicy) ||
      APPLICATION_DEFAULT_DATA.syncPolicy,
    applicationMetadata: (entity?.spec
      ?.applicationMetadata as KubernetesMetadata) || {
      annotations: [],
      labels: [],
    },
    imageUpdater: entity?.spec?.imageUpdaterConfig as
      | ImageUpdaterConfig
      | undefined,
    applicationRef: stringifyEntityRef(entity),
  };
};
